import { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ChevronDown, ChevronUp, ArrowRight, ArrowLeft, Check, CheckCircle, MessageCircle, Copy, Lock, Calendar, AlertTriangle, FileCheck, Mail, Download } from 'lucide-react';
import { toast } from 'sonner';
import { analyticsAPI, softLeadsAPI } from '@/lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COUNTRY_CODES = [
  { code: '+91', label: 'India (+91)' },
  { code: '+1', label: 'US (+1)' },
  { code: '+44', label: 'UK (+44)' },
  { code: '+971', label: 'UAE (+971)' },
  { code: '+65', label: 'Singapore (+65)' },
  { code: '+61', label: 'Australia (+61)' },
  { code: '+49', label: 'Germany (+49)' },
  { code: '+81', label: 'Japan (+81)' },
  { code: '+86', label: 'China (+86)' },
  { code: '+852', label: 'Hong Kong (+852)' },
  { code: '+60', label: 'Malaysia (+60)' },
  { code: '+966', label: 'Saudi (+966)' },
  { code: '+974', label: 'Qatar (+974)' },
  { code: '+27', label: 'South Africa (+27)' },
  { code: '+254', label: 'Kenya (+254)' },
];

function isValidEmail(email) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email); }
function isValidPhone(phone) { return /^\d{6,15}$/.test(phone.replace(/[\s\-()]/g, '')); }

const FY_TABLE = [
  { fy: '2017-18', fsDue: new Date(2018, 9, 30), arDue: new Date(2018, 10, 29) },
  { fy: '2018-19', fsDue: new Date(2019, 9, 30), arDue: new Date(2019, 10, 29) },
  { fy: '2019-20', fsDue: new Date(2020, 9, 30), arDue: new Date(2020, 10, 29) },
  { fy: '2020-21', fsDue: new Date(2021, 9, 30), arDue: new Date(2021, 10, 29) },
  { fy: '2021-22', fsDue: new Date(2022, 9, 30), arDue: new Date(2022, 10, 29) },
  { fy: '2022-23', fsDue: new Date(2023, 9, 30), arDue: new Date(2023, 10, 29) },
  { fy: '2023-24', fsDue: new Date(2024, 9, 30), arDue: new Date(2024, 10, 29) },
  { fy: '2024-25', fsDue: new Date(2025, 9, 30), arDue: new Date(2025, 10, 29) },
  { fy: '2025-26', fsDue: new Date(2026, 9, 30), arDue: new Date(2026, 10, 29) },
];

const LAST_FILED_OPTIONS = [
  'FY 2016-17', 'FY 2017-18', 'FY 2018-19', 'FY 2019-20',
  'FY 2020-21', 'FY 2021-22', 'FY 2022-23', 'FY 2023-24', 'FY 2024-25',
];

const CESSATION_OPTIONS = [
  'FY 2017-18', 'FY 2018-19', 'FY 2019-20',
  'FY 2020-21', 'FY 2021-22', 'FY 2022-23', 'FY 2023-24', 'FY 2024-25', 'FY 2025-26',
];

function daysDiff(from, to) { return Math.ceil((to.getTime() - from.getTime()) / 86400000); }
function fmt(n) { return '\u20B9' + Math.round(n).toLocaleString('en-IN'); }
function fmtLakhs(n) {
  const lakhs = Math.floor(n / 100000);
  if (lakhs >= 1) return `\u20B9 ${lakhs},00,000+`;
  const k = Math.round(n / 1000) * 1000;
  return `\u20B9 ${k.toLocaleString('en-IN')}+`;
}

function OptionCard({ selected, onClick, label, testId }) {
  return (
    <button type="button" onClick={onClick}
      className={`w-full text-left px-5 py-4 rounded-xl text-sm font-medium transition-all duration-200 min-h-[52px] ${
        selected
          ? 'bg-[#E84C3D] text-white shadow-md ring-2 ring-[#E84C3D]/30'
          : 'bg-white border border-slate-200 text-slate-700 hover:border-slate-400 hover:shadow-sm active:scale-[0.98]'
      }`}
      data-testid={testId}
    >{label}</button>
  );
}

export default function CCFSCalculator({ onContinue }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [cessationFY, setCessationFY] = useState('');
  const [lastFiledMode, setLastFiledMode] = useState('');
  const [lastFiledFY, setLastFiledFY] = useState('');
  const [intent, setIntent] = useState('');
  const [calculated, setCalculated] = useState(false);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [animating, setAnimating] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);

  // Date key that changes daily — forces recalculation if page is open past midnight
  const [dateKey, setDateKey] = useState(() => new Date().toDateString());
  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date().toDateString();
      if (now !== dateKey) setDateKey(now);
    }, 60000);
    return () => clearInterval(interval);
  }, [dateKey]);

  const [showReportForm, setShowReportForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [leadData, setLeadData] = useState({ name: '', email: '', phone: '', company_name: '', cin: '' });
  const [phoneCode, setPhoneCode] = useState('+91');
  const [leadLoading, setLeadLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [nudgeStep, setNudgeStep] = useState(0);
  const [ctaType, setCtaType] = useState(null); // 'whatsapp' | 'free_report'
  const [pdfUrl, setPdfUrl] = useState(null);
  const [submittedLeadId, setSubmittedLeadId] = useState(null);

  // Soft CTA state
  const [showSoftCTA, setShowSoftCTA] = useState(false);
  const [softEmail, setSoftEmail] = useState('');
  const [softSubmitted, setSoftSubmitted] = useState(false);
  const [softLoading, setSoftLoading] = useState(false);

  // Session ID for analytics (persists across wizard steps)
  const sessionIdRef = useRef(null);
  if (!sessionIdRef.current) sessionIdRef.current = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const sessionId = sessionIdRef.current;
  const trackedRef = useRef({ started: false, completed: false });

  const resultRef = useRef(null);
  const formFirstFieldRef = useRef(null);
  const wizardCardRef = useRef(null);
  const nextBtn1Ref = useRef(null);
  const nextBtn2Ref = useRef(null);
  const calcBtnRef = useRef(null);
  const savingsRef = useRef(null);
  const cessationRef = useRef(null);

  const isNoIdea = lastFiledMode === 'noidea';

  // Derive assumed filing year when "Not sure" is selected
  const assumedFilingFY = useMemo(() => {
    if (!isNoIdea) return null;
    if (status === 'closed' && cessationFY) {
      // Use cessation year - 1 so there's at least 1 pending FY for Close scenarios
      const cessYearStr = cessationFY.replace('FY ', '').split('-')[0];
      const cessYear = parseInt(cessYearStr, 10);
      return `FY ${cessYear - 1}-${String(cessYear).slice(2)}`;
    }
    if (status === 'running') {
      const currentYear = new Date().getFullYear();
      const twoYearsAgo = currentYear - 2;
      return `FY ${twoYearsAgo - 1}-${String(twoYearsAgo).slice(2)}`;
    }
    return null;
  }, [isNoIdea, status, cessationFY]);

  // Effective last filed FY: either user-selected or assumed
  const effectiveLastFiledFY = isNoIdea ? (assumedFilingFY || '') : lastFiledFY;
  const assumedReason = status === 'closed' ? 'based on your business stop year' : 'estimated as 2 years prior';

  const canCalculate = status && effectiveLastFiledFY && intent;
  const totalSteps = 3;

  const scrollPanelTop = () => {
    setTimeout(() => { wizardCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 100);
  };

  const canGoStep2 = status === 'running' || (status === 'closed' && cessationFY !== '');
  const canGoStep3 = lastFiledFY !== '' || isNoIdea;

  // Auto-scroll cessation section into view when "Business stopped" is selected
  useEffect(() => {
    if (status === 'closed') {
      setTimeout(() => cessationRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 150);
    }
  }, [status]);

  // Nudge arrow: trigger when a CTA button becomes enabled
  useEffect(() => {
    if (wizardStep === 1 && canGoStep2) {
      setNudgeStep(1);
      const t = setTimeout(() => setNudgeStep(0), 3000);
      // Gap 1: Auto-scroll Next button into view when cessation FY selected
      setTimeout(() => nextBtn1Ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 150);
      return () => clearTimeout(t);
    }
  }, [canGoStep2, wizardStep]);
  useEffect(() => {
    if (wizardStep === 2 && canGoStep3) {
      setNudgeStep(2);
      const t = setTimeout(() => setNudgeStep(0), 3000);
      // Gap 3: Auto-scroll Next button into view when filing FY selected
      setTimeout(() => nextBtn2Ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 150);
      return () => clearTimeout(t);
    }
  }, [canGoStep3, wizardStep]);
  useEffect(() => {
    if (wizardStep === 3 && intent) {
      setNudgeStep(3);
      const t = setTimeout(() => setNudgeStep(0), 3000);
      // Scroll Calculate button into view
      setTimeout(() => calcBtnRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 150);
      return () => clearTimeout(t);
    }
  }, [intent, wizardStep]);

  const goToStep = (step) => {
    setWizardStep(step); setCalculated(false); setSubmitted(false); setShowReportForm(false);
    // Gap 2: Scroll wizard card top into view on step change
    setTimeout(() => wizardCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' }), 100);
  };

  // Auto-focus first field when form opens
  useEffect(() => {
    if (showReportForm && formFirstFieldRef.current) {
      setTimeout(() => formFirstFieldRef.current.focus(), 100);
    }
  }, [showReportForm]);

  const handleNextStep2 = () => {
    setWizardStep(3); scrollPanelTop();
  };

  const handleCalculate = () => {
    setAnimating(true);
    setCalculated(true);
    if (!trackedRef.current.completed) {
      trackedRef.current.completed = true;
      analyticsAPI.trackEvent('calculator_completed', {
        status, cessation_fy: cessationFY, last_filed: effectiveLastFiledFY, intent, is_not_sure: isNoIdea
      }, sessionId);
    }
    setTimeout(() => {
      setAnimating(false);
      // Scroll to top of the result panel so user sees inputs + savings headline
      if (resultRef.current) resultRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 400);
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!leadData.company_name.trim()) errors.company_name = 'Required';
    if (!leadData.cin.trim()) errors.cin = 'CIN is required';
    else if (!/^[LU][0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[0-9]{6}$/.test(leadData.cin.trim().toUpperCase())) errors.cin = 'Invalid CIN format. Expected: L/U + 5 digits + 2 letters (state) + 4 digits (year) + 3 letters (type) + 6 digits';
    if (!leadData.name.trim()) errors.name = 'Required';
    if (!leadData.email.trim()) errors.email = 'Required';
    else if (!isValidEmail(leadData.email)) errors.email = 'Enter a valid email address';
    if (!leadData.phone.trim()) errors.phone = 'Required';
    else if (!isValidPhone(leadData.phone)) errors.phone = 'Enter a valid phone number (6-15 digits)';
    if (Object.keys(errors).length > 0) { setFormErrors(errors); toast.error('Please fix the highlighted fields'); return; }
    setFormErrors({});
    setLeadLoading(true);
    try {
      const fullPhone = `${phoneCode} ${leadData.phone}`;
      const shouldGeneratePdf = ctaType === 'whatsapp';
      const enrichedPayload = {
        ...leadData,
        phone: fullPhone,
        cin: leadData.cin.trim().toUpperCase(),
        quiz_answers: [status, effectiveLastFiledFY || 'noidea', intent],
        recommendation: 'calculator_lead',
        source: 'ccfs_calculator',
        cessation_fy: cessationFY || null,
        estimated_savings: result?.totalSaving || null,
        estimated_normal_fee: result?.totalNormal || null,
        estimated_ccfs_fee: result?.totalCCFS || null,
        assumed_filing_fy: isNoIdea ? (assumedFilingFY || null) : null,
        is_not_sure: isNoIdea,
        cta_type: ctaType,
        generate_pdf: shouldGeneratePdf,
      };
      const resp = await fetch(`${API_URL}/api/ccfs-leads`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(enrichedPayload)
      });
      const respData = await resp.json();
      const returnedPdfUrl = respData?.pdf_url ? `${window.location.origin}${respData.pdf_url}` : null;
      setPdfUrl(returnedPdfUrl);
      setSubmittedLeadId(respData?.id);
      setSubmitted(true);
      setShowReportForm(false);
      analyticsAPI.trackEvent('lead_submitted', { source: 'ccfs_calculator', intent, cta_type: ctaType }, sessionId);

      if (ctaType === 'whatsapp' && returnedPdfUrl) {
        const companyName = leadData.company_name || 'my company';
        const savingsText = result?.totalSaving ? fmt(result.totalSaving) : '';
        const msg = `*CCFS-2026 Assessment: ${companyName}*${savingsText ? `\nEstimated Savings: ${savingsText}` : ''}\n\nView full assessment report:\n${returnedPdfUrl}\n\nRun your own company assessment (free):\nhttps://ratnatrraya.consulting/ccfs-2026-company-status-check\n\n_Powered by R-PACE | Ratnatrraya Consulting_`;
        window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
      }
      if (ctaType === 'premium') {
        const msg = `Hi, I want a premium verified detailed report for my company and options under CCFS 2026.\n\nCompany: ${leadData.company_name || 'Not provided'}\nCIN: ${leadData.cin || 'Not provided'}\nSavings Estimate: ${fmt(result?.totalSaving || 0)}\nContact: ${leadData.name}, ${leadData.email}`;
        window.open(`https://wa.me/919000347833?text=${encodeURIComponent(msg)}`, '_blank');
      }
    } catch { toast.error('Something went wrong. Please try again.'); }
    finally { setLeadLoading(false); }
  };

  // Full calculation result
  const result = useMemo(() => {
    if (!calculated || !canCalculate) return null;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const lastFYCode = effectiveLastFiledFY.replace('FY ', '');
    let pendingFYs = FY_TABLE.filter(d => d.fy > lastFYCode);
    // Apply cessation filter for Close intent with stopped companies
    if (intent === 'close' && status === 'closed' && cessationFY) {
      const cessFYCode = cessationFY.replace('FY ', '');
      pendingFYs = pendingFYs.filter(d => d.fy <= cessFYCode);
    }
    if (pendingFYs.length === 0) return { empty: true };
    const fyDetails = pendingFYs.map(d => {
      const fsDelay = Math.max(0, daysDiff(d.fsDue, today) + 1);
      const arDelay = Math.max(0, daysDiff(d.arDue, today));
      const totalDelay = fsDelay + arDelay;
      const normalFee = totalDelay * 100;
      const ccfsFee = Math.round(normalFee * 0.10);
      return { fy: d.fy, fsDelay, arDelay, totalDelay, normalFee, ccfsFee, saving: normalFee - ccfsFee };
    });
    return { fyDetails, totalNormal: fyDetails.reduce((s, d) => s + d.normalFee, 0), totalCCFS: fyDetails.reduce((s, d) => s + d.ccfsFee, 0), totalSaving: fyDetails.reduce((s, d) => s + d.saving, 0) };
  }, [calculated, canCalculate, status, cessationFY, effectiveLastFiledFY, intent, dateKey]);

  // Intermediate (evolving) estimate for right panel
  const evolving = useMemo(() => {
    if (!effectiveLastFiledFY) return null;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const lastFYCode = effectiveLastFiledFY.replace('FY ', '');
    const pendingFYs = FY_TABLE.filter(d => d.fy > lastFYCode);
    const totalNormal = pendingFYs.reduce((sum, d) => {
      const fsDelay = Math.max(0, daysDiff(d.fsDue, today) + 1);
      const arDelay = Math.max(0, daysDiff(d.arDue, today));
      return sum + (fsDelay + arDelay) * 100;
    }, 0);
    const approxSaving = Math.round(totalNormal * 0.9);
    const approxCCFS = Math.round(totalNormal * 0.10);
    return { saving: approxSaving, normal: totalNormal, ccfs: approxCCFS };
  }, [effectiveLastFiledFY, dateKey]);

  // Status label
  const statusLabel = status === 'closed' ? `Business stopped${cessationFY ? ` (${cessationFY})` : ''}` : status === 'running' ? 'Business is active' : '';
  const intentLabel = intent === 'continue' ? 'Continue business' : intent === 'close' ? 'Close company' : '';

  // ─── RIGHT PANEL CONTENT (dynamic, non-calculated state) ───
  const RightPanelDynamic = () => (
    <Card className="shadow-lg h-full" data-testid="right-panel-dynamic">
      <CardContent className="p-5 md:p-6 space-y-5">
        {/* Section 1: Your Inputs */}
        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Your Inputs</p>
          {!status ? (
            <p className="text-sm text-slate-400 italic">Your inputs will appear here</p>
          ) : (
            <div className="space-y-1.5">
              <div className="flex justify-between items-center">
                <p className="text-sm text-slate-700"><span className="text-slate-400">Status:</span> {statusLabel}</p>
                <button onClick={() => goToStep(1)} className="text-xs text-[#E84C3D] hover:underline" data-testid="edit-step-1">Edit</button>
              </div>
              {effectiveLastFiledFY && (
                <div className="flex justify-between items-center">
                  <p className="text-sm text-slate-700">
                    <span className="text-slate-400">{isNoIdea ? 'Assumed last filing:' : 'Last filing:'}</span> {effectiveLastFiledFY}
                    {isNoIdea && <span className="text-xs text-slate-400 italic ml-1">({assumedReason})</span>}
                  </p>
                  <button onClick={() => goToStep(2)} className="text-xs text-[#E84C3D] hover:underline" data-testid="edit-step-2">Edit</button>
                </div>
              )}
              {intent && (
                <div className="flex justify-between items-center">
                  <p className="text-sm text-slate-700"><span className="text-slate-400">Action:</span> {intentLabel}</p>
                  <button onClick={() => goToStep(3)} className="text-xs text-[#E84C3D] hover:underline" data-testid="edit-step-3">Edit</button>
                </div>
              )}
            </div>
          )}
        </div>

        <hr className="border-slate-100" />

        {/* Section 2: Estimated Savings (evolving) */}
        <div data-testid="evolving-savings">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Your Estimated Savings</p>
          {!status ? (
            <>
              <p className="text-2xl font-bold text-green-700/20 mb-1" style={{ filter: 'blur(10px)' }}>₹ 3,50,000</p>
              <p className="text-xs text-slate-400">Answer the questions to reveal your estimate</p>
            </>
          ) : wizardStep === 1 ? (
            <>
              <p className="text-2xl font-bold text-green-700/30 mb-1" style={{ filter: 'blur(10px)' }}>₹ 3,50,000</p>
              <p className="text-xs text-slate-400">Getting closer... continue to Step 2</p>
            </>
          ) : effectiveLastFiledFY && evolving ? (
            wizardStep === 3 && intent ? (
              <>
                <p className="text-2xl font-bold text-green-700 mb-1 transition-all duration-700" style={{ filter: 'blur(4px)' }}>{fmtLakhs(evolving.saving)}</p>
                <p className="text-xs text-[#E84C3D] font-medium animate-pulse">Hit Calculate to reveal your savings</p>
              </>
            ) : wizardStep === 3 ? (
              <>
                <p className="text-2xl font-bold text-green-700 mb-1 transition-all duration-700" style={{ filter: 'blur(6px)' }}>{fmtLakhs(evolving.saving)}</p>
                <p className="text-xs text-slate-400">One more step to reveal...</p>
              </>
            ) : (
              <>
                <p className="text-2xl font-bold text-green-700 mb-1 transition-all duration-700" style={{ filter: 'blur(10px)' }}>{fmtLakhs(evolving.saving)}</p>
                <p className="text-xs text-slate-400">Your savings are being estimated...</p>
              </>
            )
          ) : (
            <>
              <p className="text-2xl font-bold text-green-700/20 mb-1" style={{ filter: 'blur(10px)' }}>₹ 3,50,000</p>
              <p className="text-xs text-slate-400">Estimating based on your inputs...</p>
            </>
          )}
        </div>

        <hr className="border-slate-100" />

        {/* Section 3: Pay Now vs Later (approx) */}
        <div className="transition-all duration-700" style={{ filter: evolving && wizardStep >= 2 ? (wizardStep === 3 && intent ? 'blur(4px)' : wizardStep === 3 ? 'blur(6px)' : 'blur(10px)') : 'blur(10px)' }}>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
              <p className="text-[10px] text-green-600 mb-1">You Pay Now (Under CCF Scheme)</p>
              <p className="text-base font-bold text-green-700">
                {evolving ? fmtLakhs(evolving.ccfs) : '₹ X,XXX'}
              </p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-center">
              <p className="text-[10px] text-slate-500 mb-1">You May Pay Later (If You Delay)</p>
              <p className="text-base font-bold text-slate-800">
                {evolving ? fmtLakhs(evolving.normal) : '₹ X,XX,XXX'}
              </p>
            </div>
          </div>
        </div>

        <hr className="border-slate-100" />

        {/* Section 4: 3-Tier CTA Preview (greyed out goal post) */}
        {isNoIdea && wizardStep === 2 ? (
          <div data-testid="not-sure-reassurance">
            <p className="text-sm font-semibold text-slate-800 mb-1">Not sure about your filings? That's okay.</p>
            <p className="text-xs text-slate-500 mb-3">We'll estimate your savings based on a reasonable assumption and refine it in your detailed report.</p>
            <ul className="space-y-2 mb-3">
              {['We assume a typical filing gap based on your inputs', 'You\'ll see an estimated savings range', 'Your final report will confirm exact details'].map(t => (
                <li key={t} className="flex items-start gap-2 text-xs text-slate-600"><Check className="w-3.5 h-3.5 text-green-600 shrink-0 mt-0.5" /><span>{t}</span></li>
              ))}
            </ul>
            <p className="text-xs text-green-600 font-medium">Continue to see your estimated savings</p>
          </div>
        ) : (
          <div data-testid="right-panel-cta-preview">
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-3 text-center">
              <Lock className="w-3 h-3 inline mr-1 -mt-0.5" />Complete all 3 steps to unlock
            </p>
            <div className="space-y-2.5 opacity-40 pointer-events-none select-none">
              {/* Tier 1 Preview */}
              <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-lg p-3">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-slate-200 text-slate-400 text-[10px] font-bold shrink-0">1</span>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-500">Share on WhatsApp</p>
                  <p className="text-[10px] text-slate-400">Share your PDF assessment instantly</p>
                </div>
              </div>
              {/* Tier 2 Preview */}
              <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-lg p-3">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-slate-200 text-slate-400 text-[10px] font-bold shrink-0">2</span>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-500">Free MCA Verified Report</p>
                  <p className="text-[10px] text-slate-400">We verify your data with MCA records</p>
                </div>
              </div>
              {/* Tier 3 Preview */}
              <div className="flex items-center gap-3 bg-amber-50/50 border border-amber-200/50 rounded-lg p-3">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-slate-200 text-slate-400 text-[10px] font-bold shrink-0">3</span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-slate-500">Premium Report</p>
                    <span className="text-[9px] font-bold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">&#x20B9;999</span>
                  </div>
                  <p className="text-[10px] text-slate-400">Full 5-year analysis with action plan</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="mb-12">
      <div className="text-center mb-8">
        <h2 className="font-secondary text-2xl md:text-3xl font-semibold text-slate-900 mb-3">
          Check Your Company Reset Options in 30 Seconds
        </h2>
        <p className="text-slate-600 mb-6">Answer 3 simple questions to estimate your savings and next steps.</p>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Here's what you'll see in 30 seconds</p>
        <div className="max-w-3xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
            <p className="text-[11px] text-slate-500 font-medium mb-1">Your Estimated Savings</p>
            <p className="text-xl font-bold text-green-700 mb-1">₹ 8,60,000+</p>
            <p className="text-[10px] text-slate-400">Based on your filing gaps and penalty exposure</p>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
            <p className="text-[11px] text-slate-500 font-medium mb-1">Recommended Action</p>
            <p className="text-xl font-bold text-slate-800 mb-1">Close / Dormant / Restart</p>
            <p className="text-[10px] text-slate-400">Clear next step based on your situation</p>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
            <p className="text-[11px] text-slate-500 font-medium mb-1">What to Do Next</p>
            <p className="text-xl font-bold text-slate-800 mb-1">Step-by-step plan</p>
            <p className="text-[10px] text-slate-400">No confusing compliance rules</p>
          </div>
        </div>
        <p className="text-xs text-slate-400">No technical knowledge needed. Takes less than 30 seconds.</p>
      </div>

      <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT PANEL - WIZARD */}
        <Card className={`shadow-lg transition-all duration-500 ${calculated && result ? 'opacity-50' : ''}`} ref={wizardCardRef}>
          <CardContent className="p-6 md:p-8">
            <div className="mb-6">
              <p className="text-sm font-semibold text-slate-900 mb-2">{calculated ? 'All steps complete' : `Step ${wizardStep} of ${totalSteps}`}</p>
              <div className="flex gap-1.5">
                {Array.from({ length: totalSteps }).map((_, i) => (
                  <div key={i} className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${i < wizardStep || calculated ? 'bg-[#E84C3D]' : 'bg-slate-200'}`} />
                ))}
              </div>
            </div>

            {/* COLLAPSED SUMMARY after calculation */}
            {calculated && result ? (
              <div className="space-y-4 text-center pointer-events-auto" data-testid="wizard-collapsed">
                <CheckCircle className="w-10 h-10 text-green-500 mx-auto" />
                {result.empty && intent === 'close' && status === 'closed' ? (
                  <>
                    <p className="text-sm text-slate-600">Your inputs are complete. You can proceed with closure on the right.</p>
                    <Button onClick={() => { setCtaType('free_report'); setShowReportForm(true); }} className="bg-[#E84C3D] hover:bg-[#C0392B] text-sm py-4 px-6" data-testid="left-panel-cta">
                      Start My Company Closure <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                    <p className="text-slate-400 text-[10px]">We'll guide you through strike-off, documentation, and filings end-to-end.</p>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-slate-600">Your inputs are locked. View your savings and report options on the right.</p>
                    <Button onClick={() => { setCtaType('free_report'); setShowReportForm(true); }} className="bg-[#E84C3D] hover:bg-[#C0392B] text-sm py-4 px-6" data-testid="left-panel-cta">
                      Get Your Free MCA Verified Report <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                    <p className="text-slate-400 text-[10px]">Or share on WhatsApp / get premium report from the panel above</p>
                  </>
                )}
              </div>
            ) : (
            <>
            {/* STEP 1 */}
            {wizardStep === 1 && (
              <div className="space-y-5" data-testid="wizard-step-1">
                <div>
                  <h3 className="text-base font-semibold text-slate-900 mb-1">What is the current status of your company?</h3>
                  <p className="text-xs text-slate-400 mb-4">Select the option that best describes your current situation</p>
                </div>
                <div className="space-y-3">
                  <OptionCard selected={status === 'closed'} onClick={() => { setStatus('closed'); if (!trackedRef.current.started) { trackedRef.current.started = true; analyticsAPI.trackEvent('calculator_started', { first_selection: 'closed' }, sessionId); } }} label="Business stopped / no major activity" testId="status-closed" />
                  <OptionCard selected={status === 'running'} onClick={() => { setStatus('running'); setCessationFY(''); if (!trackedRef.current.started) { trackedRef.current.started = true; analyticsAPI.trackEvent('calculator_started', { first_selection: 'running' }, sessionId); } }} label="Business is active" testId="status-running" />
                </div>
                {status === 'closed' && (
                  <div className="pl-4 border-l-2 border-[#E84C3D]/20 space-y-3 pt-1" ref={cessationRef}>
                    <div>
                      <p className="text-sm font-medium text-slate-700 mb-1">When did business activity stop?</p>
                      <p className="text-xs text-slate-400 mb-3">Not sure of the exact year? Select your best estimate. Our team cross-checks with MCA records before any action is taken.</p>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {CESSATION_OPTIONS.map(fy => (
                        <OptionCard key={fy} selected={cessationFY === fy} onClick={() => setCessationFY(fy)} label={fy} testId={`cessation-${fy.replace(/\s/g, '-')}`} />
                      ))}
                    </div>
                  </div>
                )}
                <div className="relative" ref={nextBtn1Ref}>
                  {nudgeStep === 1 && (
                    <div className="absolute -top-7 left-1/2 -translate-x-1/2 flex flex-col items-center animate-bounce">
                      <span className="text-[10px] text-[#1e3a5f] font-semibold whitespace-nowrap">Tap to continue</span>
                      <ChevronDown className="w-4 h-4 text-[#1e3a5f]" />
                    </div>
                  )}
                  <Button onClick={() => goToStep(2)} disabled={!canGoStep2} className="bg-[#1e3a5f] hover:bg-[#152d4a] w-full py-5 text-base disabled:opacity-40 disabled:bg-slate-300 transition-all duration-300" data-testid="wizard-next-1">
                    Next <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
                <p className="text-center text-slate-400 text-xs">You can change your answers anytime.</p>
              </div>
            )}

            {/* STEP 2 */}
            {wizardStep === 2 && (
              <div className="space-y-5" data-testid="wizard-step-2">
                <div>
                  <h3 className="text-base font-semibold text-slate-900 mb-1">Till which year did you last file your balance sheet?</h3>
                  <p className="text-xs text-slate-400 mb-4">Select the last year you completed balance sheet filings</p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {LAST_FILED_OPTIONS.map(fy => (
                    <OptionCard key={fy} selected={lastFiledFY === fy && !isNoIdea} onClick={() => { setLastFiledFY(fy); setLastFiledMode('select'); }} label={fy} testId={`lastfiled-${fy.replace(/\s/g, '-')}`} />
                  ))}
                </div>
                <OptionCard selected={isNoIdea} onClick={() => { setLastFiledMode('noidea'); setLastFiledFY(''); }} label="Don't remember / Not sure about filings" testId="lastfiled-noidea" />
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => goToStep(1)} className="py-5 px-4" data-testid="wizard-back-2"><ArrowLeft className="h-4 w-4" /></Button>
                  <div className="flex-1 relative" ref={nextBtn2Ref}>
                    {nudgeStep === 2 && (
                      <div className="absolute -top-7 left-1/2 -translate-x-1/2 flex flex-col items-center animate-bounce">
                        <span className="text-[10px] text-[#1e3a5f] font-semibold whitespace-nowrap">Tap to continue</span>
                        <ChevronDown className="w-4 h-4 text-[#1e3a5f]" />
                      </div>
                    )}
                    <Button onClick={handleNextStep2} disabled={!canGoStep3} className="bg-[#1e3a5f] hover:bg-[#152d4a] w-full py-5 text-base disabled:opacity-40 disabled:bg-slate-300 transition-all duration-300" data-testid="wizard-next-2">
                      Next <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <p className="text-center text-slate-400 text-xs">You can change your answers anytime.</p>
              </div>
            )}

            {/* STEP 3 */}
            {wizardStep === 3 && (
              <div className="space-y-5" data-testid="wizard-step-3">
                <div>
                  <h3 className="text-base font-semibold text-slate-900 mb-1">What do you want to do with your company now?</h3>
                  <p className="text-xs text-slate-400 mb-4">This helps us estimate what filings may be required.</p>
                </div>
                <div className="space-y-3">
                  <OptionCard selected={intent === 'continue'} onClick={() => setIntent('continue')} label="Continue business (complete pending filings)" testId="intent-continue" />
                  <OptionCard selected={intent === 'close'} onClick={() => setIntent('close')} label="Close company and avoid future liabilities" testId="intent-close" />
                </div>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => goToStep(2)} className="py-5 px-4" data-testid="wizard-back-3"><ArrowLeft className="h-4 w-4" /></Button>
                  <div className="flex-1 relative" ref={calcBtnRef}>
                    {nudgeStep === 3 && (
                      <div className="absolute -top-7 left-1/2 -translate-x-1/2 flex flex-col items-center animate-bounce">
                        <span className="text-[10px] text-[#1e3a5f] font-semibold whitespace-nowrap">Tap to see savings</span>
                        <ChevronDown className="w-4 h-4 text-[#1e3a5f]" />
                      </div>
                    )}
                    <Button onClick={handleCalculate} disabled={!intent} className="bg-[#1e3a5f] hover:bg-[#152d4a] w-full py-5 text-base disabled:opacity-40 disabled:bg-slate-300 transition-all duration-300" data-testid="calc-calculate-btn">
                      Show My Savings <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <p className="text-center text-slate-400 text-xs">You can change your answers anytime.</p>
              </div>
            )}
            </>
            )}
          </CardContent>
        </Card>

        {/* RIGHT PANEL */}
        <div ref={resultRef} className={`transition-opacity duration-400 ${animating ? 'opacity-0' : 'opacity-100'}`}>
          {calculated && result && !result.empty ? (
            /* FINAL RESULT PANEL with inline form */
            <Card className="shadow-lg" data-testid="calc-result-box">
              <CardContent className="p-5 md:p-6 space-y-4">
                {/* Inputs summary */}
                <div className={submitted ? 'opacity-60' : ''}>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Your Inputs</p>
                  <div className="space-y-1">
                    <div className="flex justify-between"><p className="text-sm text-slate-700"><span className="text-slate-400">Status:</span> {statusLabel}</p>{!submitted && <button onClick={() => goToStep(1)} className="text-xs text-[#E84C3D] hover:underline">Edit</button>}</div>
                    <div className="flex justify-between">
                      <p className="text-sm text-slate-700">
                        <span className="text-slate-400">{isNoIdea ? 'Assumed last filing:' : 'Last filing:'}</span> {effectiveLastFiledFY}
                        {isNoIdea && <span className="text-xs text-slate-400 italic ml-1">({assumedReason})</span>}
                      </p>
                      {!submitted && <button onClick={() => goToStep(2)} className="text-xs text-[#E84C3D] hover:underline">Edit</button>}
                    </div>
                    <div className="flex justify-between"><p className="text-sm text-slate-700"><span className="text-slate-400">Action:</span> {intentLabel}</p>{!submitted && <button onClick={() => goToStep(3)} className="text-xs text-[#E84C3D] hover:underline">Edit</button>}</div>
                  </div>
                </div>

                <hr className="border-slate-100" />

                {/* Exact savings */}
                <div className={submitted ? 'opacity-60' : ''} ref={savingsRef}>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Your Estimated Savings (Additional Fee Reduction)</p>
                  <p className="text-3xl font-bold text-green-800 mb-1">{fmt(result.totalSaving)}</p>
                  <p className="text-sm text-green-700 font-medium">You save {fmt(result.totalSaving)} by acting within the CCFS window.</p>
                  <p className="text-xs text-slate-400 mt-1">This reflects reduction in additional late fees under CCFS 2026.</p>
                  {isNoIdea && (
                    <p className="text-xs text-slate-400 italic mt-1" data-testid="assumed-clarity">This is an estimate based on assumed filing history. Your detailed report will refine this.</p>
                  )}
                </div>

                {/* Pay Now vs Later */}
                <div className={`grid grid-cols-2 gap-3 ${submitted ? 'opacity-60' : ''}`}>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                    <p className="text-[10px] text-green-600 mb-1">You Pay Now (Under CCF Scheme)</p>
                    <p className="text-lg font-bold text-green-700">{fmt(result.totalCCFS)}</p>
                  </div>
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-center">
                    <p className="text-[10px] text-slate-500 mb-1">You May Pay Later (If You Delay)</p>
                    <p className="text-lg font-bold text-slate-800">{fmt(result.totalNormal)}</p>
                  </div>
                </div>

                {/* Strike-off benefit — only for Close intent */}
                {intent === 'close' && !submitted && (
                  <div className={`bg-blue-50 border border-blue-200 rounded-lg p-4 ${submitted ? 'opacity-60' : ''}`} data-testid="strikeoff-benefit">
                    <p className="text-sm font-semibold text-blue-800 mb-1">Additional benefit if you close your company:</p>
                    <p className="text-xs text-blue-700">Under CCFS-2026, strike-off (STK-2) government fee is reduced by 75%.</p>
                    <p className="text-xs text-blue-700 font-medium">You pay ₹2,500 instead of ₹10,000.</p>
                  </div>
                )}

                {/* ── 3-TIER CTA SYSTEM ── */}
                {!submitted && (
                  <div className="space-y-3">
                    <p className="text-xs text-slate-500 text-center">Share or get a verified analysis of your company's compliance status</p>

                    {/* Tier 1 & 2: Side by side */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {/* Tier 1: Share on WhatsApp */}
                      <Button
                        className="bg-[#25D366] hover:bg-[#1da851] text-white py-5 gap-2 text-sm"
                        data-testid="cta-whatsapp-share"
                        onClick={() => { setCtaType('whatsapp'); setShowReportForm(true); }}
                      >
                        <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                        Share on WhatsApp
                      </Button>

                      {/* Tier 2: Get Free MCA Report */}
                      <Button
                        variant="outline"
                        className="border-[#E84C3D] text-[#E84C3D] hover:bg-[#E84C3D]/5 py-5 gap-2 text-sm"
                        data-testid="cta-free-report"
                        onClick={() => { setCtaType('free_report'); setShowReportForm(true); }}
                      >
                        <FileCheck className="w-4 h-4" /> Get Your Free MCA Verified Report
                      </Button>
                    </div>
                    <p className="text-center text-slate-400 text-[10px]">We verify your last filed data with MCA records and confirm your costs & savings</p>

                    {/* Tier 3: Premium Report */}
                    <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-300 rounded-xl p-4 space-y-2.5">
                      <div className="flex items-center gap-2">
                        <span className="bg-amber-500 text-white text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide">Premium</span>
                        <p className="text-sm font-semibold text-slate-900">Detailed Verified Report</p>
                        <span className="ml-auto text-base font-bold text-amber-700">&#x20B9;999</span>
                      </div>
                      <ul className="space-y-1.5">
                        <li className="text-xs text-slate-600 flex items-start gap-2"><Check className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />Complete MCA filing status — <b>last 5 years</b></li>
                        <li className="text-xs text-slate-600 flex items-start gap-2"><Check className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />All reset options — Close / Dormant / Restart</li>
                        <li className="text-xs text-slate-600 flex items-start gap-2"><Check className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />Clear action plan with timelines & cost</li>
                      </ul>
                      <Button
                        className="w-full bg-amber-600 hover:bg-amber-700 text-white py-4 gap-2 text-sm"
                        data-testid="cta-premium-report"
                        onClick={() => { setCtaType('premium'); setShowReportForm(true); }}
                      >
                        <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                        Get Premium Report via WhatsApp
                      </Button>
                    </div>
                  </div>
                )}

                {/* FY details */}
                <div className={`text-sm text-slate-600 ${submitted ? 'opacity-60' : ''}`}>
                  <span className="font-medium text-slate-700">FYs considered: </span>
                  {result.fyDetails.map(d => d.fy).join(', ')}
                </div>

                <div className={submitted ? 'opacity-60' : ''}>
                  <button onClick={() => setShowBreakdown(!showBreakdown)} className="flex items-center gap-1 text-sm font-medium text-[#E84C3D] hover:text-[#C0392B]" data-testid="calc-toggle-breakdown">
                    FY-wise breakdown {showBreakdown ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  {showBreakdown && (
                    <div className="mt-2 border border-slate-200 rounded-lg overflow-hidden">
                      <table className="w-full text-xs">
                        <thead><tr className="bg-slate-50 text-slate-600"><th className="text-left px-3 py-2 font-medium">FY</th><th className="text-right px-3 py-2 font-medium">Delay</th><th className="text-right px-3 py-2 font-medium">Normal</th><th className="text-right px-3 py-2 font-medium">CCFS</th><th className="text-right px-3 py-2 font-medium text-green-700">Saving</th></tr></thead>
                        <tbody>
                          {result.fyDetails.map(d => (<tr key={d.fy} className="border-t border-slate-100"><td className="px-3 py-2 text-slate-700">{d.fy}</td><td className="text-right px-3 py-2 text-slate-500">{d.totalDelay}d</td><td className="text-right px-3 py-2">{fmt(d.normalFee)}</td><td className="text-right px-3 py-2">{fmt(d.ccfsFee)}</td><td className="text-right px-3 py-2 text-green-700 font-medium">{fmt(d.saving)}</td></tr>))}
                          <tr className="border-t border-slate-200 font-bold"><td className="px-3 py-2 text-slate-900">Total</td><td></td><td className="text-right px-3 py-2">{fmt(result.totalNormal)}</td><td className="text-right px-3 py-2">{fmt(result.totalCCFS)}</td><td className="text-right px-3 py-2 text-green-700">{fmt(result.totalSaving)}</td></tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                  <p className="text-slate-400 text-[10px] italic mt-2">Delay days shown are cumulative delays for filing both financial statements (AOC-4) and annual returns (MGT-7).</p>
                </div>

                <p className={`text-slate-400 text-xs italic ${submitted ? 'opacity-60' : ''}`}>
                  Indicative estimate based on selected inputs and filing date assumed as today. Final values may vary.
                </p>
                {isNoIdea && !submitted && (
                  <p className="text-slate-500 text-xs italic" data-testid="assumed-estimate-note">
                    We've estimated your filing history based on your inputs to give you a quick and useful result.
                  </p>
                )}

                {/* CONTROLLED RISK CONTEXT — Only for "Not Sure" scenarios */}
                {isNoIdea && !submitted && (
                  intent === 'close' ? (
                    /* 🔴 LEVEL 2 — HIGH RISK (Q3=Close) */
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-2.5" data-testid="risk-context-high">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                        <p className="text-sm font-semibold text-red-800">Since your filing year is not confirmed, there may be hidden compliance risks:</p>
                      </div>
                      <ul className="space-y-1.5 pl-6">
                        <li className="text-xs text-red-700 flex items-start gap-2"><span className="text-red-400 shrink-0 mt-0.5">&#x2022;</span>Non-filing for 3 consecutive years can lead to director disqualification (5 years)</li>
                        <li className="text-xs text-red-700 flex items-start gap-2"><span className="text-red-400 shrink-0 mt-0.5">&#x2022;</span>You may not be able to become a director in any company during this period</li>
                        <li className="text-xs text-red-700 flex items-start gap-2"><span className="text-red-400 shrink-0 mt-0.5">&#x2022;</span>Your DIN may become inactive, impacting future business activity</li>
                        <li className="text-xs text-red-700 flex items-start gap-2"><span className="text-red-400 shrink-0 mt-0.5">&#x2022;</span>If not resolved under CCFS, full penalties may apply later</li>
                      </ul>
                      <p className="text-xs text-red-600 font-medium italic">Since your filing year is assumed, it is important to verify this before proceeding with closure.</p>
                    </div>
                  ) : (
                    /* 🟠 LEVEL 1 — MODERATE RISK (Q3=Continue) */
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-2.5" data-testid="risk-context-moderate">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                        <p className="text-sm font-semibold text-amber-800">Since your filing status is not confirmed, there may be compliance gaps:</p>
                      </div>
                      <ul className="space-y-1.5 pl-6">
                        <li className="text-xs text-amber-700 flex items-start gap-2"><span className="text-amber-400 shrink-0 mt-0.5">&#x2022;</span>Pending filings may exist without visibility</li>
                        <li className="text-xs text-amber-700 flex items-start gap-2"><span className="text-amber-400 shrink-0 mt-0.5">&#x2022;</span>This can lead to penalties if ignored</li>
                        <li className="text-xs text-amber-700 flex items-start gap-2"><span className="text-amber-400 shrink-0 mt-0.5">&#x2022;</span>Business decisions (funding, closure, restart) may be based on incomplete data</li>
                      </ul>
                      <p className="text-xs text-amber-600 font-medium italic">We recommend verifying your status before taking the next step.</p>
                    </div>
                  )
                )}

                <hr className="border-slate-100" />

                {/* INLINE FORM / SUCCESS / SHARE */}
                {submitted ? (
                  <div data-testid="submission-success" className="space-y-5">
                    {/* SUCCESS CARD — varies by CTA type */}
                    {ctaType === 'whatsapp' ? (
                      <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                        <CheckCircle className="w-9 h-9 text-green-500 mb-3" />
                        <h4 className="text-base font-semibold text-slate-900 mb-1">Your PDF has been shared on WhatsApp</h4>
                        <p className="text-sm text-slate-600 mb-3">Your CCFS assessment PDF is ready. Share it with your CA, CS, or co-directors.</p>
                        {pdfUrl && (
                          <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline font-medium" data-testid="pdf-direct-link">
                            <Download className="w-3.5 h-3.5 inline mr-1" />Download PDF directly
                          </a>
                        )}
                      </div>
                    ) : ctaType === 'premium' ? (
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
                        <CheckCircle className="w-9 h-9 text-amber-500 mb-3" />
                        <h4 className="text-base font-semibold text-slate-900 mb-1">Your Premium Report request has been submitted</h4>
                        <p className="text-sm text-slate-600 mb-3">WhatsApp has been opened with your details. Our team will connect with you shortly to share the payment link and begin your 5-year detailed analysis.</p>
                        <p className="text-sm text-slate-500 italic">You can also call us directly at +91 90003 47833</p>
                      </div>
                    ) : (
                      <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                        <CheckCircle className="w-9 h-9 text-green-500 mb-3" />
                        <h4 className="text-base font-semibold text-slate-900 mb-1">Your Free MCA Verified Report is being prepared</h4>
                        <p className="text-sm text-slate-600 mb-3">We're verifying your MCA records and will share your detailed report within 24 hours.</p>
                        <p className="text-sm text-slate-500 italic">One of our team members may reach out if any clarification is required.</p>
                      </div>
                    )}

                    {/* What happens next? — for Free Report CTA */}
                    {ctaType === 'free_report' && (
                      <div>
                        <p className="text-sm font-semibold text-slate-800 mb-3">What happens next?</p>
                        <div className="space-y-2.5">
                          <div className="flex items-start gap-3">
                            <span className="flex items-center justify-center w-5 h-5 rounded-full bg-[#E84C3D] text-white text-[10px] font-bold shrink-0 mt-0.5">1</span>
                            <p className="text-sm text-slate-600">We review your MCA filing status and gaps</p>
                          </div>
                          <div className="flex items-start gap-3">
                            <span className="flex items-center justify-center w-5 h-5 rounded-full bg-[#E84C3D] text-white text-[10px] font-bold shrink-0 mt-0.5">2</span>
                            <p className="text-sm text-slate-600">We prepare your reset options (Close / Dormant / Restart)</p>
                          </div>
                          <div className="flex items-start gap-3">
                            <span className="flex items-center justify-center w-5 h-5 rounded-full bg-[#E84C3D] text-white text-[10px] font-bold shrink-0 mt-0.5">3</span>
                            <p className="text-sm text-slate-600">You receive a clear action plan with timelines and cost</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Book a Quick Discussion CTA */}
                    <div>
                      <Button onClick={() => navigate('/contact')} className="bg-[#E84C3D] hover:bg-[#C0392B] w-full text-sm py-5" data-testid="book-discussion-btn">
                        <Calendar className="mr-2 h-4 w-4" /> Book a Quick Discussion
                      </Button>
                      <p className="text-center text-slate-400 text-xs mt-2">Talk to us for 15 minutes to understand your best option.</p>
                    </div>

                    {/* Post-WhatsApp upsell: Free or Premium report */}
                    {ctaType === 'whatsapp' && (
                      <div className="space-y-3">
                        <hr className="border-slate-100" />
                        <p className="text-sm font-semibold text-slate-800">Want us to verify your company status?</p>

                        <Button
                          variant="outline"
                          className="w-full border-[#E84C3D] text-[#E84C3D] hover:bg-[#E84C3D]/5 py-4 gap-2 text-sm"
                          data-testid="upsell-free-report"
                          disabled={leadLoading}
                          onClick={async () => {
                            setLeadLoading(true);
                            try {
                              await fetch(`${API_URL}/api/ccfs-leads/${submittedLeadId}/request-free-report`, {
                                method: 'PATCH', headers: { 'Content-Type': 'application/json' },
                              });
                              setCtaType('free_report');
                              analyticsAPI.trackEvent('upsell_free_report', { source: 'post_whatsapp' }, sessionId);
                              toast.success('Your Free MCA Verified Report request has been submitted!');
                            } catch { toast.error('Something went wrong. Please try again.'); }
                            finally { setLeadLoading(false); }
                          }}
                        >
                          <FileCheck className="w-4 h-4" /> {leadLoading ? 'Requesting...' : 'Get Your Free MCA Verified Report'}
                        </Button>

                        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-300 rounded-xl p-4 space-y-2">
                          <div className="flex items-center gap-2">
                            <span className="bg-amber-500 text-white text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide">Premium</span>
                            <p className="text-sm font-semibold text-slate-900">Get the full 5-year analysis</p>
                            <span className="ml-auto text-base font-bold text-amber-700">&#x20B9;999</span>
                          </div>
                          <Button
                            className="w-full bg-amber-600 hover:bg-amber-700 text-white py-3 gap-2 text-sm"
                            data-testid="upsell-premium-report"
                            onClick={() => {
                              const msg = `Hi, I want a premium verified detailed report for my company and options under CCFS 2026.\n\nCompany: ${leadData.company_name || 'Not provided'}\nSavings Estimate: ${fmt(result?.totalSaving || 0)}`;
                              window.open(`https://wa.me/919000347833?text=${encodeURIComponent(msg)}`, '_blank');
                              analyticsAPI.trackEvent('premium_report_click_upsell', { savings: result?.totalSaving }, sessionId);
                            }}
                          >
                            <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                            Get Premium Report via WhatsApp
                          </Button>
                        </div>
                      </div>
                    )}

                    <hr className="border-slate-100" />

                    {/* Share section — tertiary */}
                    <div>
                      <p className="text-sm font-semibold text-slate-800 mb-1">Know someone with an inactive company?</p>
                      <p className="text-xs text-slate-500 mb-3">Share this tool — it could save them &#x20B9;50,000 to &#x20B9;20,00,000+</p>
                      <div className="flex gap-2">
                        <a href={`https://wa.me/?text=${encodeURIComponent('Check how much your inactive company can save under CCFS-2026 — this free calculator shows your exact penalty savings in 30 seconds. ' + window.location.href)}`} target="_blank" rel="noopener noreferrer" className="flex-1" data-testid="share-whatsapp">
                          <Button className="bg-[#25D366] hover:bg-[#1da851] gap-2 w-full text-sm"><MessageCircle className="w-4 h-4" /> WhatsApp</Button>
                        </a>
                        <Button variant="outline" className="gap-2 flex-1 text-sm" onClick={() => { navigator.clipboard.writeText(window.location.href); toast.success('Link copied!'); }} data-testid="share-copy-link">
                          <Copy className="w-4 h-4" /> Copy Link
                        </Button>
                      </div>
                      <p className="text-center text-slate-400 text-[10px] mt-3">No spam. No unnecessary calls. Just actionable insights.</p>
                    </div>
                  </div>
                ) : showReportForm ? (
                  <div data-testid="inline-lead-form">
                    <p className="text-sm font-semibold text-slate-800 mb-1">
                      {ctaType === 'whatsapp' ? 'Share your CCFS assessment on WhatsApp' : ctaType === 'premium' ? 'Get Your Premium Detailed Report' : 'Get Your Free MCA Verified Report'}
                    </p>
                    <p className="text-xs text-slate-500 mb-4">
                      {ctaType === 'whatsapp'
                        ? 'Enter your details to generate a branded PDF and share it instantly.'
                        : ctaType === 'premium'
                        ? 'Enter your details so our team can prepare your premium 5-year detailed report.'
                        : 'We verify your last filed data with MCA records and confirm your costs & savings.'
                      }
                    </p>
                    <form onSubmit={handleLeadSubmit} className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Company Name</label>
                        <Input ref={formFirstFieldRef} value={leadData.company_name} onChange={e => { setLeadData({ ...leadData, company_name: e.target.value }); setFormErrors(p => ({ ...p, company_name: '' })); }} placeholder="Your company name" className={`h-11 ${formErrors.company_name ? 'border-red-400' : ''}`} data-testid="form-company" />
                        {formErrors.company_name && <p className="text-red-500 text-xs mt-0.5">{formErrors.company_name}</p>}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">CIN (Corporate Identity Number)</label>
                        <Input value={leadData.cin} onChange={e => { setLeadData({ ...leadData, cin: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 21) }); setFormErrors(p => ({ ...p, cin: '' })); }} placeholder="e.g. U12345MH2020PTC123456" maxLength={21} className={`h-11 font-mono tracking-wide ${formErrors.cin ? 'border-red-400' : ''}`} data-testid="form-cin" />
                        {formErrors.cin && <p className="text-red-500 text-xs mt-0.5">{formErrors.cin}</p>}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Your Name</label>
                        <Input value={leadData.name} onChange={e => { setLeadData({ ...leadData, name: e.target.value }); setFormErrors(p => ({ ...p, name: '' })); }} placeholder="Your name" className={`h-11 ${formErrors.name ? 'border-red-400' : ''}`} data-testid="form-name" />
                        {formErrors.name && <p className="text-red-500 text-xs mt-0.5">{formErrors.name}</p>}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Email ID</label>
                        <Input type="email" value={leadData.email} onChange={e => { setLeadData({ ...leadData, email: e.target.value }); setFormErrors(p => ({ ...p, email: '' })); }} placeholder="your@email.com" className={`h-11 ${formErrors.email ? 'border-red-400' : ''}`} data-testid="form-email" />
                        {formErrors.email && <p className="text-red-500 text-xs mt-0.5">{formErrors.email}</p>}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Mobile Number</label>
                        <div className="flex gap-2">
                          <select value={phoneCode} onChange={e => setPhoneCode(e.target.value)} className="w-[120px] shrink-0 rounded-lg border border-slate-200 bg-white px-2 py-2 text-sm text-slate-700 focus:border-[#E84C3D] focus:ring-1 focus:ring-[#E84C3D] outline-none h-11" data-testid="form-phone-code">
                            {COUNTRY_CODES.map(cc => (<option key={cc.code} value={cc.code}>{cc.label}</option>))}
                          </select>
                          <Input value={leadData.phone} onChange={e => { setLeadData({ ...leadData, phone: e.target.value }); setFormErrors(p => ({ ...p, phone: '' })); }} placeholder="98765 43210" className={`flex-1 h-11 ${formErrors.phone ? 'border-red-400' : ''}`} data-testid="form-phone" />
                        </div>
                        {formErrors.phone && <p className="text-red-500 text-xs mt-0.5">{formErrors.phone}</p>}
                      </div>
                      <Button type="submit" className={`w-full py-5 text-sm ${ctaType === 'whatsapp' ? 'bg-[#25D366] hover:bg-[#1da851]' : ctaType === 'premium' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-[#E84C3D] hover:bg-[#C0392B]'}`} disabled={leadLoading} data-testid="form-submit-btn">
                        {leadLoading
                          ? (ctaType === 'whatsapp' ? 'Generating PDF & sharing...' : ctaType === 'premium' ? 'Submitting & opening WhatsApp...' : 'Sending your report request...')
                          : (ctaType === 'whatsapp' ? 'Generate PDF & Share on WhatsApp' : ctaType === 'premium' ? 'Submit & Connect on WhatsApp' : 'Get Your Free MCA Verified Report')
                        }
                      </Button>
                      <div className="text-center space-y-0.5">
                        <p className="text-slate-400 text-[10px]">{ctaType === 'whatsapp' ? 'Your branded PDF will be generated instantly' : 'Delivered within 24 hours'} &bull; No spam. No unnecessary calls.</p>
                        <p className="text-slate-400 text-[10px]">Your details are kept strictly confidential.</p>
                      </div>
                    </form>
                  </div>
                ) : (
                  <div data-testid="calc-report-cta" className="space-y-2">
                    {/* Soft CTA — Save for Later */}
                    {!softSubmitted ? (
                      !showSoftCTA ? (
                        <button onClick={() => setShowSoftCTA(true)} className="block w-full text-center text-xs text-slate-400 hover:text-slate-600 mt-1 transition-colors" data-testid="soft-cta-trigger">
                          Not ready? Just save your result for later
                        </button>
                      ) : (
                        <div className="mt-3 bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2" data-testid="soft-cta-form">
                          <p className="text-xs text-slate-600 font-medium">Save your result — we'll email you a reminder</p>
                          <div className="flex gap-2">
                            <Input
                              type="email"
                              value={softEmail}
                              onChange={e => setSoftEmail(e.target.value)}
                              placeholder="your@email.com"
                              className="h-9 text-sm flex-1"
                              data-testid="soft-email-input"
                            />
                            <Button
                              size="sm"
                              className="bg-[#E84C3D] hover:bg-[#C0392B] h-9 px-3"
                              disabled={softLoading || !isValidEmail(softEmail)}
                              onClick={async () => {
                                setSoftLoading(true);
                                try {
                                  await softLeadsAPI.create({
                                    email: softEmail,
                                    savings_amount: result?.totalSaving || null,
                                    quiz_answers: [status, effectiveLastFiledFY || 'noidea', intent],
                                    session_id: sessionId,
                                  });
                                  setSoftSubmitted(true);
                                  analyticsAPI.trackEvent('soft_lead_captured', { email: softEmail }, sessionId);
                                  toast.success('Saved! We\'ll send you a reminder.');
                                } catch { toast.error('Something went wrong'); }
                                finally { setSoftLoading(false); }
                              }}
                              data-testid="soft-submit-btn"
                            >
                              <Mail className="w-3.5 h-3.5 mr-1" /> Save
                            </Button>
                          </div>
                          <p className="text-[10px] text-slate-400">No spam. Just a reminder to revisit your savings.</p>
                        </div>
                      )
                    ) : (
                      <p className="text-center text-green-600 text-xs mt-3 font-medium" data-testid="soft-cta-done">Saved! We'll remind you before the window closes.</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

          ) : calculated && result?.empty && intent === 'close' && status === 'closed' ? (
            /* CLOSURE-READY STATE — filings up to date + close intent */
            <Card className="shadow-lg h-full" data-testid="closure-ready-panel">
              <CardContent className="p-6 md:p-8 space-y-5">
                {/* Your Inputs */}
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Your Inputs</p>
                  <div className="space-y-1">
                    <p className="text-sm text-slate-700"><span className="text-slate-400">Status:</span> {statusLabel}</p>
                    <p className="text-sm text-slate-700"><span className="text-slate-400">Last filing:</span> {effectiveLastFiledFY}</p>
                    <p className="text-sm text-slate-700"><span className="text-slate-400">Action:</span> Close company</p>
                  </div>
                </div>

                <hr className="border-slate-100" />

                {/* Closure-ready message */}
                <div>
                  <div className="flex items-start gap-3 mb-3">
                    <FileCheck className="w-6 h-6 text-green-500 shrink-0 mt-0.5" />
                    <h4 className="text-base font-semibold text-slate-900">Your company appears ready for closure</h4>
                  </div>
                  <p className="text-sm text-slate-600 mb-4">Since your filings are up to date, you can proceed directly with closing your company under the standard process.</p>

                  {/* Value context */}
                  <p className="text-sm font-medium text-slate-700 mb-2">Closure still requires:</p>
                  <ul className="space-y-2 mb-4">
                    <li className="flex items-start gap-2 text-sm text-slate-600">
                      <Check className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                      <span>Proper documentation and MCA filings</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm text-slate-600">
                      <Check className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                      <span>Verification of company status (including charges and compliance)</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm text-slate-600">
                      <Check className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                      <span>Correct selection of closure route (Strike-off / Fast Track Exit)</span>
                    </li>
                  </ul>

                  {/* Light risk */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-5">
                    <p className="text-xs text-amber-700"><AlertTriangle className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />Incorrect filings or missed steps can delay closure or lead to rejection.</p>
                  </div>
                </div>

                {/* Primary CTA */}
                {submitted ? (
                  <div data-testid="submission-success" className="space-y-5">
                    <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                      <CheckCircle className="w-9 h-9 text-green-500 mb-3" />
                      <h4 className="text-base font-semibold text-slate-900 mb-1">Your closure request has been received</h4>
                      <p className="text-sm text-slate-600 mb-3">Our team will review your company details and get back to you within 24 hours.</p>
                      <p className="text-sm text-slate-500 italic">One of our team members may reach out if any clarification is required.</p>
                    </div>
                    <div>
                      <Button onClick={() => navigate('/contact')} className="bg-[#E84C3D] hover:bg-[#C0392B] w-full text-sm py-5" data-testid="book-discussion-btn">
                        <Calendar className="mr-2 h-4 w-4" /> Book a Quick Discussion
                      </Button>
                      <p className="text-center text-slate-400 text-xs mt-2">Talk to us for 15 minutes to understand your best option.</p>
                    </div>
                  </div>
                ) : showReportForm ? (
                  <div data-testid="inline-lead-form">
                    <p className="text-sm font-semibold text-slate-800 mb-1">Start your company closure</p>
                    <p className="text-xs text-slate-500 mb-4">We'll handle your closure end-to-end — documentation, filings, and follow-ups.</p>
                    <form onSubmit={handleLeadSubmit} className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Company Name</label>
                        <Input ref={formFirstFieldRef} value={leadData.company_name} onChange={e => { setLeadData({ ...leadData, company_name: e.target.value }); setFormErrors(p => ({ ...p, company_name: '' })); }} placeholder="Your company name" className={`h-11 ${formErrors.company_name ? 'border-red-400' : ''}`} data-testid="form-company" />
                        {formErrors.company_name && <p className="text-red-500 text-xs mt-0.5">{formErrors.company_name}</p>}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">CIN (Corporate Identity Number)</label>
                        <Input value={leadData.cin} onChange={e => { setLeadData({ ...leadData, cin: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 21) }); setFormErrors(p => ({ ...p, cin: '' })); }} placeholder="e.g. U12345MH2020PTC123456" maxLength={21} className={`h-11 font-mono tracking-wide ${formErrors.cin ? 'border-red-400' : ''}`} data-testid="form-cin-closure" />
                        {formErrors.cin && <p className="text-red-500 text-xs mt-0.5">{formErrors.cin}</p>}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Your Name</label>
                        <Input value={leadData.name} onChange={e => { setLeadData({ ...leadData, name: e.target.value }); setFormErrors(p => ({ ...p, name: '' })); }} placeholder="Your name" className={`h-11 ${formErrors.name ? 'border-red-400' : ''}`} data-testid="form-name" />
                        {formErrors.name && <p className="text-red-500 text-xs mt-0.5">{formErrors.name}</p>}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Email ID</label>
                        <Input type="email" value={leadData.email} onChange={e => { setLeadData({ ...leadData, email: e.target.value }); setFormErrors(p => ({ ...p, email: '' })); }} placeholder="your@email.com" className={`h-11 ${formErrors.email ? 'border-red-400' : ''}`} data-testid="form-email" />
                        {formErrors.email && <p className="text-red-500 text-xs mt-0.5">{formErrors.email}</p>}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Mobile Number</label>
                        <div className="flex gap-2">
                          <select value={phoneCode} onChange={e => setPhoneCode(e.target.value)} className="w-[120px] shrink-0 rounded-lg border border-slate-200 bg-white px-2 py-2 text-sm text-slate-700 focus:border-[#E84C3D] focus:ring-1 focus:ring-[#E84C3D] outline-none h-11" data-testid="form-phone-code">
                            {COUNTRY_CODES.map(cc => (<option key={cc.code} value={cc.code}>{cc.label}</option>))}
                          </select>
                          <Input value={leadData.phone} onChange={e => { setLeadData({ ...leadData, phone: e.target.value }); setFormErrors(p => ({ ...p, phone: '' })); }} placeholder="98765 43210" className={`flex-1 h-11 ${formErrors.phone ? 'border-red-400' : ''}`} data-testid="form-phone" />
                        </div>
                        {formErrors.phone && <p className="text-red-500 text-xs mt-0.5">{formErrors.phone}</p>}
                      </div>
                      <Button type="submit" className="w-full bg-[#E84C3D] hover:bg-[#C0392B] py-5 text-sm" disabled={leadLoading} data-testid="form-submit-btn">
                        {leadLoading ? 'Submitting...' : 'Start My Company Closure Process'}
                      </Button>
                    </form>
                  </div>
                ) : (
                  <div>
                    <Button onClick={() => setShowReportForm(true)} className="bg-[#E84C3D] hover:bg-[#C0392B] w-full text-sm py-5 shadow-md ring-2 ring-[#E84C3D]/20 animate-pulse hover:animate-none" data-testid="closure-start-btn">
                      Start My Company Closure Process <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                    <p className="text-center text-slate-500 text-xs mt-2">We'll handle your closure end-to-end — documentation, filings, and follow-ups.</p>
                    <Button variant="outline" onClick={() => navigate('/contact')} className="w-full text-sm mt-3" data-testid="closure-checklist-btn">
                      Get Closure Checklist First
                    </Button>
                    {/* Soft CTA for closure-ready */}
                    {!softSubmitted ? (
                      !showSoftCTA ? (
                        <button onClick={() => setShowSoftCTA(true)} className="block w-full text-center text-xs text-slate-400 hover:text-slate-600 mt-3 transition-colors" data-testid="soft-cta-trigger-closure">
                          Not ready? Save this for later
                        </button>
                      ) : (
                        <div className="mt-3 bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2" data-testid="soft-cta-form-closure">
                          <p className="text-xs text-slate-600 font-medium">Save your result — we'll email you a reminder</p>
                          <div className="flex gap-2">
                            <Input type="email" value={softEmail} onChange={e => setSoftEmail(e.target.value)} placeholder="your@email.com" className="h-9 text-sm flex-1" data-testid="soft-email-closure" />
                            <Button size="sm" className="bg-[#E84C3D] hover:bg-[#C0392B] h-9 px-3" disabled={softLoading || !isValidEmail(softEmail)}
                              onClick={async () => {
                                setSoftLoading(true);
                                try {
                                  await softLeadsAPI.create({ email: softEmail, savings_amount: 0, quiz_answers: [status, effectiveLastFiledFY || 'noidea', intent], session_id: sessionId });
                                  setSoftSubmitted(true);
                                  analyticsAPI.trackEvent('soft_lead_captured', { email: softEmail, type: 'closure_ready' }, sessionId);
                                  toast.success('Saved! We\'ll send you a reminder.');
                                } catch { toast.error('Something went wrong'); }
                                finally { setSoftLoading(false); }
                              }}
                              data-testid="soft-submit-closure"
                            ><Mail className="w-3.5 h-3.5 mr-1" /> Save</Button>
                          </div>
                          <p className="text-[10px] text-slate-400">No spam. Just a reminder to revisit.</p>
                        </div>
                      )
                    ) : (
                      <p className="text-center text-green-600 text-xs mt-3 font-medium">Saved! We'll remind you before the window closes.</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

          ) : calculated && result?.empty ? (
            <Card className="shadow-lg h-full">
              <CardContent className="p-6 md:p-8 flex flex-col items-center justify-center text-center h-full min-h-[380px]">
                <p className="text-slate-700 text-base mb-4">Based on your inputs, there are no pending FYs to calculate savings for.</p>
                <p className="text-slate-500 text-sm mb-6">If you believe filings are pending, try selecting an earlier last-filed FY.</p>
                <Button onClick={() => goToStep(2)} className="bg-[#E84C3D] hover:bg-[#C0392B]">Change Last Filed FY <ArrowRight className="ml-2 h-4 w-4" /></Button>
              </CardContent>
            </Card>

          ) : (
            /* Dynamic right panel during wizard */
            <RightPanelDynamic />
          )}
        </div>
      </div>

      {/* Mobile sticky savings bar */}
      {!calculated && !submitted && status && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 shadow-lg z-40 px-4 py-2.5 flex items-center justify-between" data-testid="mobile-sticky-bar">
          <div>
            <p className="text-[10px] text-slate-500">Est. Savings</p>
            <p className="text-base font-bold text-green-700">{evolving ? fmtLakhs(evolving.saving) : '₹ X,XX,XXX'}</p>
          </div>
          <p className="text-xs text-slate-400">Step {wizardStep} of {totalSteps}</p>
        </div>
      )}

    </div>
  );
}
